/**
 * /portfolio-data — Portfolio Data (#17 frontend). Admin-only.
 *
 * Monthly portfolio metrics (AuM, net flow, fee ex-VAT, return) per
 * numbered agreement. Three areas:
 *   - Bulk upload (Section A)                          — Inc 2
 *   - Download blank template (button in the card)     — Inc 1
 *   - Manual single-month entry / history editor with
 *     a Read/Edit lifecycle + the stored-months table  — Inc 3
 *
 * Backend: /agreement-monthly-metrics — Admin writes, open reads. The route
 * uses the AdminUser dependency (a dedicated permission may replace it later),
 * so this page gates on the admin role to match. Design:
 * _MEMORY/decisions/2026-06-18_portfolio-data-page-design.md. (A richer
 * Reports/MIS view of this series is a near-term follow-up — Pedro 2026-06-19.)
 *
 * Section A mirrors the two-phase backend: select → preview (writes nothing) →
 * review → confirm (commit). The backend is STATELESS between calls (it
 * re-parses the file each time), so the chosen File is held in state and
 * RE-SENT on commit.
 *
 * Section B follows the app's §3g/§3h lifecycle: the stored-months table is the
 * Read view; clicking a row or picking a month enters Edit; Save/Cancel return
 * to Read; the editor's dirty state is registered with the global
 * DirtyFormProvider so leaving (or switching agreement/month) while dirty
 * prompts the standard "Unsaved changes" guard.
 *
 * Money is integer cents on the wire — shown as € (÷100) and entered in € (×100,
 * rounded half-up to the cent); return_ratio is a decimal ratio — shown/entered
 * as a percentage (×100), never as cents.
 */
import {
  Alert, Badge, Button, Card, Container, Divider, Group, List, Modal, NumberInput,
  Paper, Select, Stack, Stepper, Table, Tabs, Text, Title, Tooltip,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconAlertTriangle, IconCalendarTime, IconChartLine, IconCheck,
  IconDownload, IconFileUpload, IconPencil, IconTrash, IconTrendingUp,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { agreementsApi } from '../api/agreements';
import { monthlyMetricsApi } from '../api/monthlyMetrics';
import type {
  AgreementMonthlyMetric, MetricsImportConflict, MetricsImportReport,
} from '../api/monthlyMetrics';
import { accountMetricsApi } from '../api/accountMetrics';
import { portfolioExportApi } from '../api/portfolioExport';
import type {
  AccountMetricsImportConflict, AccountMetricsImportReport,
} from '../api/accountMetrics';
import { BackToCompliance } from '../components/BackToCompliance';
import { ADMIN_PAGE_READ_ROLES } from '../api/types';
import { ReturnsSection } from './ReturnsPage';
import { XlsxDropzone } from '../components/XlsxDropzone';
import { MonthFlagsAlert } from '../components/MonthFlagsAlert';
import { useAlertModal } from '../components/AlertModalProvider';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { useCurrentUser } from '../hooks/useAuth';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// ── money / month helpers ────────────────────────────────────────────────
const ym = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthKeyOf = (iso: string) => iso.slice(0, 7);
// Agreement-level manual entry is HISTORICAL only. From the cut-off on, agreement
// AuM / flows / returns / fees are DERIVED from per-account inputs, never hand-entered
// (backend single source of truth: fee_periods.FEE_HISTORY_CUTOFF_MONTH; the /agreement-
// monthly-metrics POST hard-rejects computed-era months). Mirror it here so the editor is
// gated at the source with a reason, instead of failing at Save with a raw 400 (§3h/§3j).
const COMPUTED_ERA_FROM = '2026-01';                       // 'YYYY-MM'
const isComputedEraMonth = (monthKey: string) => monthKey >= COMPUTED_ERA_FROM; // lexical
const LAST_HISTORICAL_MONTH = new Date(2025, 11, 1);        // Dec-2025 — maxDate for the picker
const eurFmt = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' });
const fmtEur = (cents: number | null) => (cents == null ? '—' : eurFmt.format(cents / 100));
const fmtPct = (r: number | string | null) => (r == null ? '—' : `${(Number(r) * 100).toFixed(2)}%`);
// € (number|'') → integer cents, half-up; blank → null.
const toCents = (euros: number | string) =>
  (euros === '' || euros == null ? null : Math.round(Number(euros) * 100));
// percent (number|'') → decimal ratio; blank → null.
const pctToRatio = (p: number | string) =>
  (p === '' || p == null ? null : Number(p) / 100);

// ── Section B edit-form value helpers (Read/Edit lifecycle) ───────────────
// Fee ex-VAT is NOT edited here (2026-07-04): the Portfolio Data page is EOM metrics
// (AuM / flows / return); the whole fee history lives on the Fees page. `fee_ex_vat` on
// the stored row is PRESERVED on save (see the save mutation), never wiped by an edit here.
type EditVals = {
  aum: number | string; flow: number | string; ret: number | string;
};
const BLANK_VALS: EditVals = { aum: '', flow: '', ret: '' };
const centsToVal = (c: number | null) => (c == null ? '' : c / 100);
// Flows are shown/edited as ONE signed net (+ inflow / − outflow), consistent with the
// upload template + the per-account returns store; the stored inflow/outflow magnitudes
// are collapsed to net for display and split back on save. Both-null → no flow (blank).
const netFlowCents = (r: AgreementMonthlyMetric): number | null =>
  (r.inflow_eur_cents == null && r.outflow_eur_cents == null)
    ? null
    : (r.inflow_eur_cents ?? 0) - (r.outflow_eur_cents ?? 0);
const valsFromRow = (r: AgreementMonthlyMetric): EditVals => ({
  aum: r.aum_eur_cents / 100,
  flow: centsToVal(netFlowCents(r)),
  ret: r.return_ratio == null ? '' : Number(r.return_ratio) * 100,
});
const normNum = (v: number | string) => (v === '' || v == null ? '' : Number(v));
const sameVals = (a: EditVals, b: EditVals) =>
  (['aum', 'flow', 'ret'] as const)
    .every((k) => Object.is(normNum(a[k]), normNum(b[k])));

// ── Page (nav reorg 2026-07-04, Inc 4) ─────────────────────────────────────
// The former hub + "20th/EOM" split (2026-06-21) is replaced by ONE URL-driven
// tabbed page (mirroring FeesPage): Returns · EOM AuM & flows · Manual edit ·
// History upload · Export. The per-account 20th-of-month AuM (the fee BASIS) lives
// on the Fees page, not here. ReturnsPage is folded in — /admin/returns now
// redirects to the Returns tab. The returns series is shown seamlessly, with the
// Computed/Historical badge kept as the one deliberate provenance marker (Pedro
// 2026-07-04). Design: _MEMORY/decisions/2026-07-04_phase1-metrics-derivation-and-
// portfolio-nav.md.

/** /admin/portfolio-data — the Portfolio Data workspace (Admin-only, tabbed). */
export function PortfolioDataPage() {
  useDocumentTitle('AuM & Returns');
  const { user } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirmDiscardAsync } = useDirtyForm();
  const urlTab = searchParams.get('tab') ?? 'returns';

  // Mirror FeesPage: the dirty-form guard wraps tab switching so leaving the
  // Manual-edit tab mid-edit prompts the standard "Unsaved changes" guard.
  const setActiveTab = (v: string | null) => {
    if (!v) return;
    void confirmDiscardAsync().then((ok) => {
      if (!ok) return;
      const next = new URLSearchParams(searchParams);
      if (v === 'returns') next.delete('tab'); else next.set('tab', v);
      setSearchParams(next, { replace: true });
    });
  };

  if (!user) return null;
  // P45 (Pedro 2026-10-15): RM and Compliance read the Returns tab only; the upload and
  // export tabs are Admin's (hidden for them, and refused by the server).
  if (!ADMIN_PAGE_READ_ROLES.includes(user.role)) return <AccessDeniedAlert />;
  const isAdmin = user.role === 'admin';
  const tab = isAdmin ? urlTab : 'returns';

  return (
    <Container size="xl">
      <Stack>
        {isAdmin
          ? <BackToCompliance to="/admin" label="Back to Admin" />
          : <BackToCompliance to="/" label="Back to Welcome" />}
        <Group gap="xs"><IconChartLine size={22} /><Title order={2}>AuM & Returns</Title></Group>
        <Text c="dimmed">
          Per-account end-of-month AuM, cash flows, and the returns computed from them —
          uploaded history (before 2026) blended seamlessly with in-app computed values
          (Jan-2026 onward).
        </Text>

        <Tabs value={tab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="returns" leftSection={<IconTrendingUp size={14} />}>Returns</Tabs.Tab>
            {isAdmin && <>
              <Tabs.Tab value="eom" leftSection={<IconCalendarTime size={14} />}>Upload month (Jan 2026 &rarr;)</Tabs.Tab>
              <Tabs.Tab value="history" leftSection={<IconFileUpload size={14} />}>Upload history (&rarr; Dec 2025)</Tabs.Tab>
              <Tabs.Tab value="export" leftSection={<IconDownload size={14} />}>Export series</Tabs.Tab>
            </>}
          </Tabs.List>

          <Tabs.Panel value="returns" pt="md"><ReturnsSection /></Tabs.Panel>
          {isAdmin && <>
            <Tabs.Panel value="eom" pt="md"><AccountEomFlowsSection onViewReturns={() => setActiveTab('returns')} /></Tabs.Panel>
            <Tabs.Panel value="history" pt="md"><HistoryUploadTabs /></Tabs.Panel>
            <Tabs.Panel value="export" pt="md"><PortfolioExportSection /></Tabs.Panel>
          </>}
        </Tabs>
      </Stack>
    </Container>
  );
}

/** Tab 3 "Upload history (-> Dec 2025)" — the two pre-2026 seeding tools behind
 *  sub-tabs. Manual edit registers dirty state, so guard the sub-tab switch through
 *  confirmDiscardAsync (the query-param-not-pathname gap, C-248). */
function HistoryUploadTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirmDiscardAsync } = useDirtyForm();
  const inner = searchParams.get('htab') ?? 'bulk';
  const setInner = (v: string | null) => {
    if (!v) return;
    void confirmDiscardAsync().then((ok) => {
      if (!ok) return;
      const next = new URLSearchParams(searchParams);
      if (v === 'bulk') next.delete('htab'); else next.set('htab', v);
      setSearchParams(next, { replace: true });
    });
  };
  return (
    <Tabs value={inner} onChange={setInner} keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="bulk">Bulk upload</Tabs.Tab>
        <Tabs.Tab value="manual">Manual edit</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="bulk" pt="md"><BulkUploadSection /></Tabs.Panel>
      <Tabs.Panel value="manual" pt="md"><ManualEntrySection /></Tabs.Panel>
    </Tabs>
  );
}

/** Download the 8-tab blended portfolio export over a chosen month window (Inc 7).
 *  Agreements in rows, months in columns; uploaded history blended with in-app
 *  computed values; money in euros + percentages, 2 dp. */
function PortfolioExportSection() {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function handle() {
    if (!from || !to) return;
    setDownloading(true);
    try {
      await portfolioExportApi.download(ym(from), ym(to));
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Export failed',
        message: err instanceof Error ? err.message : 'The export could not be generated.',
      });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card withBorder padding="lg">
      <Stack>
        <div>
          <Title order={4}>Portfolio export</Title>
          <Text c="dimmed" size="sm">
            Download the 9-tab workbook (EOM AuM · EOM AuM per account · Net flow · Return · 20th
            AuM · Fee % · Fee % wVAT · Fee € xVAT · Fee € wVAT) — agreements in rows
            (accounts in rows on the per-account tab), months in columns, oldest to newest —
            blending uploaded history (before Jan-2026) with in-app computed values (Jan-2026
            onward). The per-account tab starts at Jan-2026: there is no per-account history before it.
          </Text>
        </div>
        <Group align="flex-end">
          <MonthPickerInput label="From" placeholder="First month" value={from} onChange={setFrom} maw={200} />
          <MonthPickerInput label="To" placeholder="Last month" value={to} onChange={setTo} maw={200} />
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handle}
            loading={downloading}
            disabled={!from || !to}
          >
            Download export
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ── Section A — bulk upload ───────────────────────────────────────────────
function BulkUploadSection() {
  const [downloading, setDownloading] = useState(false);
  const [step, setStep] = useState(0);                // 0 Select · 1 Review · 2 Done
  const [file, setFile] = useState<File | null>(null); // RETAINED for commit re-send
  const [preview, setPreview] = useState<MetricsImportReport | null>(null);
  const [result, setResult] = useState<MetricsImportReport | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);

  function resetUpload() {
    setStep(0); setFile(null); setPreview(null); setResult(null);
    setPreviewing(false); setCommitting(false);
  }

  async function handleSelect(picked: File) {
    setFile(picked);
    setPreviewing(true);
    try {
      const report = await monthlyMetricsApi.importFile(picked, 'preview', 'skip');
      setPreview(report);
      setStep(1);
    } catch (err) {
      setFile(null);
      notifications.show({
        color: 'red', title: 'Could not read the workbook',
        message: err instanceof Error ? err.message : 'The file could not be processed.',
      });
    } finally {
      setPreviewing(false);
    }
  }

  async function handleCommit(onConflict: MetricsImportConflict) {
    if (!file) return; // defensive — the File must still be in hand (stateless backend)
    setCommitting(true);
    try {
      const report = await monthlyMetricsApi.importFile(file, 'commit', onConflict);
      setResult(report);
      setStep(2);
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Import failed',
        message: err instanceof Error ? err.message : 'The import could not be completed.',
      });
    } finally {
      setCommitting(false);
    }
  }

  async function handleDownloadTemplate() {
    setDownloading(true);
    try {
      await monthlyMetricsApi.downloadTemplate();
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Template download failed',
        message: err instanceof Error ? err.message : 'The template could not be generated.',
      });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Title order={4}>Bulk upload</Title>
            <Text c="dimmed" size="sm">
              Upload the firm monthly-metrics workbook (3 sheets — AuM · Flows · Monthly
              Returns; Flows is the signed net monthly flow, + inflow / − outflow — one row
              per agreement, CIIF + mnemonic identify each row, month-end dates across the
              columns, entered as real dates, not text). Review the reconciliation, then import.
              History only — months from Jan-2026 onward are computed in-app and are
              rejected here.
            </Text>
          </div>
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadTemplate}
            loading={downloading}
            style={{ flexShrink: 0 }}
          >
            Download blank template
          </Button>
        </Group>

        <Stepper active={step}>
          <Stepper.Step label="Select" description="Choose the workbook">
            <Stack pt="md">
              <XlsxDropzone
                hint="One Excel file: the firm monthly-metrics workbook or the blank template, filled in."
                onSelect={handleSelect}
                loading={previewing}
                onReject={() => notifications.show({
                  color: 'red', title: 'Not accepted',
                  message: 'Please choose a single .xlsx workbook.',
                })}
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Review" description="Check the reconciliation">
            <Stack pt="md">
              {preview && <ReviewPanel report={preview} fileName={file?.name ?? ''} />}
              {preview && (
                <ConfirmActions
                  report={preview}
                  committing={committing}
                  onCommit={handleCommit}
                  onCancel={resetUpload}
                />
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Done" description="Result">
            <Stack pt="md">
              {result && <ResultPanel report={result} />}
              <Group>
                <Button variant="light" onClick={resetUpload}>Upload another workbook</Button>
              </Group>
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Stack>
    </Card>
  );
}

// ── Per-account 20th-of-month AuM (fee basis) — per-account arc Inc 1c ─────
// A round-trip distinct from the agreement bulk upload above: pick a month, the
// app emits one row per contributing account (identity pre-filled, AuM blank),
// the user fills the 20th-AuM and uploads; preview → commit (skip/overwrite).
export function AccountAumSection({ onGoToCompute }: { onGoToCompute?: (month: string) => void } = {}) {
  const [period, setPeriod] = useState<Date | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [step, setStep] = useState(0);                 // 0 Select · 1 Review · 2 Done
  const [file, setFile] = useState<File | null>(null); // RETAINED for commit re-send
  const [preview, setPreview] = useState<AccountMetricsImportReport | null>(null);
  const [result, setResult] = useState<AccountMetricsImportReport | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [confirm, setConfirm] = useState<AccountMetricsImportConflict | null>(null);  // commit-confirm modal
  const [handoffOpen, setHandoffOpen] = useState(false);  // "20th-AuM saved → Compute" hand-off

  function resetUpload() {
    setStep(0); setFile(null); setPreview(null); setResult(null);
    setPreviewing(false); setCommitting(false); setConfirm(null); setHandoffOpen(false);
  }

  const monthLabel = period
    ? period.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : '';

  // Download is GENERIC now (no month) — the template is the same for every month.
  async function handleDownload() {
    setDownloading(true);
    try {
      await accountMetricsApi.downloadTemplate();
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Template download failed',
        message: err instanceof Error ? err.message : 'The template could not be generated.',
      });
    } finally { setDownloading(false); }
  }

  // The picked month is AUTHORITATIVE and travels with every upload call.
  async function handleSelect(picked: File) {
    if (!period) {
      notifications.show({ color: 'red', title: 'Pick the import month first', message: 'Choose the month this file is for (above), then upload.' });
      return;
    }
    setFile(picked); setPreviewing(true);
    try {
      const report = await accountMetricsApi.importFile(picked, 'preview', 'skip', ym(period));
      setPreview(report); setStep(1);
    } catch (err) {
      setFile(null);
      notifications.show({
        color: 'red', title: 'Could not read the workbook',
        message: err instanceof Error ? err.message : 'The file could not be processed.',
      });
    } finally { setPreviewing(false); }
  }

  async function handleCommit(onConflict: AccountMetricsImportConflict) {
    if (!file || !period) return; // defensive — File must still be in hand (stateless backend)
    setCommitting(true);
    try {
      const report = await accountMetricsApi.importFile(file, 'commit', onConflict, ym(period));
      setResult(report); setStep(2); setConfirm(null);
      if (onGoToCompute) setHandoffOpen(true);
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Import failed',
        message: err instanceof Error ? err.message : 'The import could not be completed.',
      });
    } finally { setCommitting(false); }
  }

  const plural = (n: number) => (n === 1 ? '' : 's');

  return (
    <Stack gap="lg">
      {/* ── Download box — the GENERIC template (no month; month is chosen at upload) ── */}
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Title order={4}>Download a template</Title>
            <Text c="dimmed" size="sm">
              Download the template (one row per contributing account, identity filled) and
              enter each account's AuM on the 20th. The month is chosen when you upload — the
              template itself is the same for every month.
            </Text>
          </div>
          <Button
            variant="light" leftSection={<IconDownload size={16} />}
            onClick={handleDownload} loading={downloading} style={{ flexShrink: 0 }}
          >
            Download template
          </Button>
        </Group>
      </Paper>

      {/* ── Upload box — the month is chosen HERE and is authoritative (Pedro 2026-07-11) ── */}
      <Paper withBorder p="lg" radius="md">
        <div>
          <Title order={4}>Upload per-account 20th-of-month AuM (fee basis)</Title>
          <Text c="dimmed" size="sm">
            Pick the month this file is for — the month you pick here is the month imported
            (one upload = one month), regardless of any date inside the file. Then upload the
            filled template.
          </Text>
        </div>

        <Group align="flex-end" mt="md">
          <MonthPickerInput
            label="Import month" placeholder="Pick the month this file is for"
            value={period} onChange={(d) => { setPeriod(d); resetUpload(); }}
            minDate={new Date('2025-12-01T00:00:00')}
            maw={280}
          />
        </Group>

        <Stepper active={step} mt="md">
          <Stepper.Step label="Select" description="Upload the filled file">
            <Stack pt="md">
              {period ? (
                <XlsxDropzone
                  hint="One Excel file: the template downloaded above, filled in for the month you picked."
                  onSelect={handleSelect}
                  loading={previewing}
                  onReject={() => notifications.show({
                    color: 'red', title: 'Not accepted',
                    message: 'Please choose a single .xlsx workbook.',
                  })}
                />
              ) : (
                <Alert color="blue" variant="light">Pick the import month above to enable upload.</Alert>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Review" description="Check the reconciliation">
            <Stack pt="md">
              {preview && (
                <Stack gap="sm">
                  {file?.name && (
                    <Text size="sm" c="dimmed">{file.name} → importing as <b>{monthLabel}</b></Text>
                  )}
                  <Text>
                    <b>{preview.matched_accounts}</b> account-row{plural(preview.matched_accounts)} matched ·{' '}
                    <b>{preview.new_count}</b> new ·{' '}
                    <b>{preview.conflict_count}</b> already present ·{' '}
                    <b>{preview.unmatched_rows.length}</b> unmatched ·{' '}
                    <b>{preview.errors.length}</b> error{plural(preview.errors.length)}
                  </Text>

                  <MonthFlagsAlert preview={preview} kind="fee" />

                  {(preview.month_mismatch_warnings?.length ?? 0) > 0 && (
                    <Alert
                      color="orange" variant="light" icon={<IconAlertTriangle size={16} />}
                      title="This file looks like it was built for a different month"
                    >
                      <List size="sm" spacing={2}>
                        {preview.month_mismatch_warnings!.map((w, i) => <List.Item key={i}>{w}</List.Item>)}
                      </List>
                    </Alert>
                  )}

                  {preview.unmatched_rows.length > 0 && (
                    <Alert
                      color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}
                      title={`${preview.unmatched_rows.length} row${plural(preview.unmatched_rows.length)} referenced no live Active account — ignored`}
                    >
                      <Text size="sm">{preview.unmatched_rows.join(', ')}</Text>
                    </Alert>
                  )}

                  {preview.errors.length > 0 && (
                    <Alert
                      color="red" variant="light" icon={<IconAlertCircle size={16} />}
                      title={`${preview.errors.length} row${plural(preview.errors.length)} could not be read — skipped`}
                    >
                      <List size="sm" spacing={2}>
                        {preview.errors.slice(0, 50).map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                      </List>
                    </Alert>
                  )}

                  {preview.conflict_count > 0 && (
                    <Alert color="blue" variant="light" title={`${preview.conflict_count} account-month${plural(preview.conflict_count)} already exist for ${monthLabel}`}>
                      <Text size="sm" c="dimmed">
                        Showing first {preview.conflict_sample.length} of {preview.conflict_count}:
                      </Text>
                      <Text size="sm">{preview.conflict_sample.join('  ·  ')}</Text>
                    </Alert>
                  )}

                  <Group>
                    {preview.conflict_count > 0 ? (
                      <>
                        <Button loading={committing} onClick={() => setConfirm('skip')}>
                          Import new only (keep existing)
                        </Button>
                        <Button color="orange" variant="light" loading={committing} onClick={() => setConfirm('overwrite')}>
                          Overwrite existing
                        </Button>
                      </>
                    ) : (
                      <Button loading={committing} disabled={preview.new_count === 0} onClick={() => setConfirm('skip')}>
                        Import {preview.new_count} new
                      </Button>
                    )}
                    <Button variant="subtle" onClick={resetUpload}>Cancel</Button>
                  </Group>
                </Stack>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Done" description="Result">
            <Stack pt="md">
              {result && (
                <Alert color="green" variant="light" icon={<IconCheck size={16} />} title={`Import complete for ${monthLabel}`}>
                  <Text size="sm">
                    {result.inserted} inserted · {result.overwritten} overwritten · {result.skipped} skipped.
                  </Text>
                </Alert>
              )}
              {/* Clear the picker too, so the next import forces a deliberate re-pick
                  (guards against inadvertently overwriting the month just imported). */}
              <Group>
                <Button variant="light" onClick={() => { resetUpload(); setPeriod(null); }}>
                  Import another file
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Paper>

      {/* Commit confirm — names the month (#5); challenges before overwriting (#6). */}
      <Modal
        opened={confirm !== null} onClose={() => setConfirm(null)}
        title={confirm === 'overwrite' ? `Overwrite existing data for ${monthLabel}?` : `Import for ${monthLabel}?`}
      >
        {preview && confirm && (
          <Stack gap="sm">
            {confirm === 'overwrite' ? (
              <Text size="sm">
                There is already information on record for <b>{monthLabel}</b>. Proceeding will{' '}
                <b>overwrite {preview.conflict_count} existing account-month{plural(preview.conflict_count)}</b>
                {preview.new_count > 0 && <> and import <b>{preview.new_count} new</b></>} for {monthLabel}.
                The previous values are replaced and cannot be recovered.
              </Text>
            ) : (
              <Text size="sm">
                Import <b>{preview.new_count} new account-month{plural(preview.new_count)}</b> for <b>{monthLabel}</b>
                {preview.conflict_count > 0 && <>; the {preview.conflict_count} already on record are kept unchanged</>}.
              </Text>
            )}
            {!!preview.material_flow_warnings?.length && (
              <Alert
                color="bordeaux" variant="light" icon={<IconAlertTriangle size={16} />}
                title={`${preview.material_flow_warnings.length} agreement-month return${plural(preview.material_flow_warnings.length)} will be withheld`}
              >
                <Text size="sm">
                  {preview.material_flow_warnings.length} flow{plural(preview.material_flow_warnings.length)} above 10% of
                  opening AuM {preview.material_flow_warnings.length === 1 ? 'has' : 'have'} no mtd%. The AuM &amp; flows
                  will import, but those agreement-months' returns will NOT be calculated until you supply the mtd%.
                  Cancel to fix the file, or Proceed.
                </Text>
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                color={preview.material_flow_warnings?.length ? 'bordeaux' : (confirm === 'overwrite' ? 'orange' : undefined)}
                loading={committing} onClick={() => handleCommit(confirm)}
              >
                {preview.material_flow_warnings?.length
                  ? `Proceed — ${confirm === 'overwrite' ? 'overwrite' : 'import'} for ${monthLabel}`
                  : `${confirm === 'overwrite' ? 'Overwrite' : 'Import'} for ${monthLabel}`}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* MR-style hand-off (#4): after a successful import, offer the next workflow step. */}
      <Modal opened={handoffOpen} onClose={() => setHandoffOpen(false)} title="20th-AuM imported" centered>
        <Stack>
          <Text size="sm">
            The 20th-of-month AuM for {monthLabel} is saved. Compute the month&apos;s fees next?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setHandoffOpen(false)}>Stay here</Button>
            <Button onClick={() => { setHandoffOpen(false); if (period) onGoToCompute?.(`${ym(period)}-01`); }}>Compute fees →</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ── Per-account EOM-AuM + flows (returns basis ≥ Jan-2026) — arc Inc 2 ─────
// A round-trip distinct from BOTH the agreement bulk upload (historical, < Jan-26) and the
// per-account 20th-AuM (fee basis, on the Fees page). Two boxes: download the GENERIC template
// (no month), then upload — the month is chosen at UPLOAD via the picker and is authoritative
// (Pedro 2026-07-11). Any flow date not in the picked month rejects the WHOLE upload. Writes
// the dedicated returns store, independent of the 20th-AuM.
export function AccountEomFlowsSection({ onViewReturns }: { onViewReturns?: () => void }) {
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [period, setPeriod] = useState<Date | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [step, setStep] = useState(0);                 // 0 Select · 1 Review · 2 Done
  const [file, setFile] = useState<File | null>(null); // RETAINED for commit re-send
  const [preview, setPreview] = useState<AccountMetricsImportReport | null>(null);
  const [result, setResult] = useState<AccountMetricsImportReport | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [confirm, setConfirm] = useState<AccountMetricsImportConflict | null>(null);  // commit-confirm modal

  function resetUpload() {
    setStep(0); setFile(null); setPreview(null); setResult(null);
    setPreviewing(false); setCommitting(false); setConfirm(null);
  }

  const monthLabel = period
    ? period.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : '';

  async function handleDownload() {
    setDownloading(true);
    try {
      await accountMetricsApi.downloadEomTemplate();
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Template download failed',
        message: err instanceof Error ? err.message : 'The template could not be generated.',
      });
    } finally { setDownloading(false); }
  }

  async function handleSelect(picked: File) {
    if (!period) {
      notifications.show({ color: 'red', title: 'Pick the import month first', message: 'Choose the month this file is for (above), then upload.' });
      return;
    }
    setFile(picked); setPreviewing(true);
    try {
      const report = await accountMetricsApi.importEomFile(picked, 'preview', 'skip', ym(period));
      setPreview(report); setStep(1);
    } catch (err) {
      setFile(null);
      notifications.show({
        color: 'red', title: 'Could not read the workbook',
        message: err instanceof Error ? err.message : 'The file could not be processed.',
      });
    } finally { setPreviewing(false); }
  }

  async function handleCommit(onConflict: AccountMetricsImportConflict) {
    if (!file || !period) return; // defensive — File must still be in hand (stateless backend)
    setCommitting(true);
    try {
      // proceedMaterialGaps=true: the confirm modal (which shows any material-flow warnings) IS the
      // operator's explicit Proceed (§5.4). The backend still gates an un-confirmed API commit.
      const report = await accountMetricsApi.importEomFile(file, 'commit', onConflict, ym(period), true);
      setConfirm(null);
      // A commit can be HARD-REJECTED server-side (flow-date or same-date-MTD conflict) and
      // return a 200 report that WROTE NOTHING — never show "saved" in that case (the silent-
      // no-write bug, 2026-07-15). Surface the rejection on the review step instead.
      const wasRejected =
        (report.flow_date_errors?.length ?? 0) > 0 || (report.mtd_errors?.length ?? 0) > 0;
      setResult(report);
      if (wasRejected) {
        setPreview(report); setStep(1);
        notifications.show({
          color: 'red', title: 'Upload rejected — nothing was saved',
          message: 'The workbook has blocking issues (shown above). Fix them and re-upload.',
        });
      } else {
        setStep(2); setHandoffOpen(true);
      }
    } catch (err) {
      notifications.show({
        color: 'red', title: 'Import failed',
        message: err instanceof Error ? err.message : 'The import could not be completed.',
      });
    } finally { setCommitting(false); }
  }

  const plural = (n: number) => (n === 1 ? '' : 's');
  const flowRejected = (preview?.flow_date_errors?.length ?? 0) > 0;
  const mtdRejected = (preview?.mtd_errors?.length ?? 0) > 0;
  const rejected = flowRejected || mtdRejected;

  return (
    <Stack gap="lg">
      <Modal opened={handoffOpen} onClose={() => setHandoffOpen(false)} title="Returns updated" centered>
        <Stack>
          <Text size="sm">
            The end-of-month AuM and flows for {monthLabel || 'the month'} are saved.
            Returns recompute automatically from them.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setHandoffOpen(false)}>Stay here</Button>
            <Button onClick={() => { setHandoffOpen(false); onViewReturns?.(); }}>View returns</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Download box — the GENERIC template (no month; month is chosen at upload) ── */}
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Title order={4}>Download a template</Title>
            <Text c="dimmed" size="sm">
              Download the template (one row per contributing account, identity filled) and enter
              each account's end-of-month AuM and the month's inflow/outflow (positive amounts, each
              with a date in the upload month). The month is chosen when you upload — the template is
              the same for every month. Drives the Jan-2026-onward returns.
            </Text>
          </div>
          <Button
            variant="light" leftSection={<IconDownload size={16} />}
            onClick={handleDownload} loading={downloading} style={{ flexShrink: 0 }}
          >
            Download template
          </Button>
        </Group>
      </Paper>

      {/* ── Upload box — the month is chosen HERE and is authoritative (Pedro 2026-07-11) ── */}
      <Paper withBorder p="lg" radius="md">
        <div>
          <Title order={4}>Upload per-account EOM AuM &amp; flows (returns basis)</Title>
          <Text c="dimmed" size="sm">
            Pick the month this file is for — it is the month imported (one upload = one month).
            Every flow you enter must be dated within that month, or the upload is rejected. Then
            upload the filled template.
          </Text>
        </div>

        <Group align="flex-end" mt="md">
          <MonthPickerInput
            label="Import month" placeholder="Pick the month this file is for"
            value={period} onChange={(d) => { setPeriod(d); resetUpload(); }}
            minDate={new Date('2025-12-01T00:00:00')}
            maw={280}
          />
        </Group>

        <Stepper active={step} mt="md">
          <Stepper.Step label="Select" description="Upload the filled file">
            <Stack pt="md">
              {period ? (
                <XlsxDropzone
                  hint="One Excel file: the template downloaded above, filled in for the month you picked."
                  onSelect={handleSelect}
                  loading={previewing}
                  onReject={() => notifications.show({
                    color: 'red', title: 'Not accepted',
                    message: 'Please choose a single .xlsx workbook.',
                  })}
                />
              ) : (
                <Alert color="blue" variant="light">Pick the import month above to enable upload.</Alert>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Review" description="Check the reconciliation">
            <Stack pt="md">
              {preview && (
                <Stack gap="sm">
                  {file?.name && (
                    <Text size="sm" c="dimmed">{file.name} → importing as <b>{monthLabel}</b></Text>
                  )}
                  <Text>
                    <b>{preview.matched_accounts}</b> account-row{plural(preview.matched_accounts)} matched ·{' '}
                    <b>{preview.new_count}</b> new ·{' '}
                    <b>{preview.conflict_count}</b> already have EOM ·{' '}
                    <b>{preview.unmatched_rows.length}</b> unmatched ·{' '}
                    <b>{preview.errors.length}</b> error{plural(preview.errors.length)}
                    {(((preview.flows_new_count ?? 0) + (preview.flows_changed_count ?? 0)) > 0) && (
                      <> ·{' '}<b>{(preview.flows_new_count ?? 0) + (preview.flows_changed_count ?? 0)}</b> with new/changed flows</>
                    )}
                  </Text>

                  <MonthFlagsAlert preview={preview} kind="eom" />

                  {flowRejected && (
                    <Alert
                      color="red" variant="filled" icon={<IconAlertCircle size={16} />}
                      title={`Upload rejected — ${preview.flow_date_errors!.length} flow date${plural(preview.flow_date_errors!.length)} not in ${monthLabel}`}
                    >
                      <List size="sm" spacing={2}>
                        {preview.flow_date_errors!.slice(0, 50).map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                      </List>
                      <Text size="sm" mt={6}>
                        Every flow must carry a date inside {monthLabel}. Fix the flagged rows in the
                        workbook (a blank flow date is not allowed) and re-upload — nothing was written.
                      </Text>
                    </Alert>
                  )}

                  {mtdRejected && (
                    <Alert
                      color="red" variant="filled" icon={<IconAlertCircle size={16} />}
                      title={`Upload rejected — ${preview.mtd_errors!.length} conflicting agreement MTD${plural(preview.mtd_errors!.length)}`}
                    >
                      <List size="sm" spacing={2}>
                        {preview.mtd_errors!.slice(0, 50).map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                      </List>
                      <Text size="sm" mt={6}>
                        The month-to-date return is <b>one figure per agreement per date</b>. When two
                        account rows of the same agreement share a flow date, give them the <b>same</b>{' '}
                        MTD, then re-upload — nothing was written.
                      </Text>
                    </Alert>
                  )}

                  {(preview.month_mismatch_warnings?.length ?? 0) > 0 && (
                    <Alert
                      color="orange" variant="light" icon={<IconAlertTriangle size={16} />}
                      title="This file looks like it was built for a different month"
                    >
                      <List size="sm" spacing={2}>
                        {preview.month_mismatch_warnings!.map((w, i) => <List.Item key={i}>{w}</List.Item>)}
                      </List>
                    </Alert>
                  )}

                  {!!preview.reconciliation_warnings?.length && (
                    <Alert
                      color="orange" variant="light" icon={<IconAlertTriangle size={16} />}
                      title="Reconciliation — sum of per-account EOM vs the agreement-level AuM"
                    >
                      <List size="sm" spacing={2}>
                        {preview.reconciliation_warnings.map((w, i) => <List.Item key={i}>{w}</List.Item>)}
                      </List>
                      <Text size="xs" c="dimmed" mt={4}>
                        Heads-up only — the import isn't blocked. Check the per-account EOM figures
                        against the uploaded agreement history (typically the Dec-2025 seam).
                      </Text>
                    </Alert>
                  )}

                  {preview.unmatched_rows.length > 0 && (
                    <Alert
                      color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}
                      title={`${preview.unmatched_rows.length} row${plural(preview.unmatched_rows.length)} not applied (no live Active account)`}
                    >
                      <Text size="sm">{preview.unmatched_rows.join(', ')}</Text>
                    </Alert>
                  )}

                  {preview.errors.length > 0 && (
                    <Alert
                      color="red" variant="light" icon={<IconAlertCircle size={16} />}
                      title={`${preview.errors.length} row${plural(preview.errors.length)} could not be read — skipped`}
                    >
                      <List size="sm" spacing={2}>
                        {preview.errors.slice(0, 50).map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                      </List>
                    </Alert>
                  )}

                  {preview.conflict_count > 0 && (
                    <Alert color="blue" variant="light" title={`${preview.conflict_count} account-month${plural(preview.conflict_count)} already carry EOM for ${monthLabel}`}>
                      <Text size="sm" c="dimmed">
                        Showing first {preview.conflict_sample.length} of {preview.conflict_count}:
                      </Text>
                      <Text size="sm">{preview.conflict_sample.join('  ·  ')}</Text>
                    </Alert>
                  )}

                  {(((preview.flows_new_count ?? 0) + (preview.flows_changed_count ?? 0)) > 0) && (
                    <Alert
                      color="teal" variant="light" icon={<IconTrendingUp size={16} />}
                      title={`Flows in this file — ${preview.flows_new_count ?? 0} new · ${preview.flows_changed_count ?? 0} changed`}
                    >
                      <Text size="sm" c="dimmed">
                        Account-months that gain a flow not stored today (<b>new</b>), or whose stored flows
                        differ from the file (<b>changed</b>). New-flow rows apply on import; changes to
                        account-months that already have EOM take effect only if you choose{' '}
                        <b>Overwrite existing months</b>.
                      </Text>
                      {(preview.flows_new_sample?.length ?? 0) > 0 && (
                        <Text size="sm" mt={6}><b>New flows:</b>{'  '}{preview.flows_new_sample!.join('  ·  ')}</Text>
                      )}
                      {(preview.flows_changed_sample?.length ?? 0) > 0 && (
                        <Text size="sm" mt={4}><b>Changed flows:</b>{'  '}{preview.flows_changed_sample!.join('  ·  ')}</Text>
                      )}
                    </Alert>
                  )}

                  {!!preview.material_flow_warnings?.length && (
                    <Alert
                      color="bordeaux" variant="light" icon={<IconAlertTriangle size={16} />}
                      title={`${preview.material_flow_warnings.length} large flow${plural(preview.material_flow_warnings.length)} without an mtd% — return${plural(preview.material_flow_warnings.length)} will be withheld`}
                    >
                      <List size="sm" spacing={2}>
                        {preview.material_flow_warnings.map((w, i) => <List.Item key={i}>{w}</List.Item>)}
                      </List>
                      <Text size="sm" mt={6}>
                        A flow above 10% of the agreement's opening (prior-month-end) AuM needs an mtd% for
                        the SAA proxy to value it. Without one, that agreement's return for the month is{' '}
                        <b>withheld</b>. Add the mtd% in the workbook and re-upload, or Proceed on commit to
                        import the AuM &amp; flows without the return.
                      </Text>
                    </Alert>
                  )}

                  {!!preview.material_flow_notes?.length && (
                    <Alert
                      color="teal" variant="light" icon={<IconCheck size={16} />}
                      title={`${preview.material_flow_notes.length} large flow${plural(preview.material_flow_notes.length)} with an mtd% — will compute`}
                    >
                      <List size="sm" spacing={2}>
                        {preview.material_flow_notes.map((w, i) => <List.Item key={i}>{w}</List.Item>)}
                      </List>
                    </Alert>
                  )}

                  {rejected ? (
                    <Group>
                      <Text size="sm" c="red">Fix the flagged issues in the workbook, then re-upload — nothing was written.</Text>
                      <Button variant="subtle" onClick={resetUpload}>Start over</Button>
                    </Group>
                  ) : (
                    <Group>
                      {preview.conflict_count > 0 ? (
                        <>
                          {/* When 0 new, "Import new only" would write NOTHING — disable it and say
                              so, steering to Overwrite (else it silently no-ops; 2026-07-15). */}
                          <Button
                            loading={committing}
                            disabled={preview.new_count === 0}
                            onClick={() => setConfirm('skip')}
                          >
                            {preview.new_count === 0
                              ? 'Import new only (0 new — nothing to import)'
                              : `Import ${preview.new_count} new only (keep existing)`}
                          </Button>
                          <Button color="orange" variant="light" loading={committing} onClick={() => setConfirm('overwrite')}>
                            Overwrite existing
                          </Button>
                        </>
                      ) : (
                        <Button loading={committing} disabled={preview.new_count === 0} onClick={() => setConfirm('skip')}>
                          Import {preview.new_count} new
                        </Button>
                      )}
                      <Button variant="subtle" onClick={resetUpload}>Cancel</Button>
                    </Group>
                  )}
                </Stack>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Done" description="Result">
            <Stack pt="md">
              {result && (
                <Alert color="green" variant="light" icon={<IconCheck size={16} />} title={`Import complete for ${monthLabel}`}>
                  <Text size="sm">
                    {result.inserted} inserted · {result.overwritten} overwritten · {result.skipped} skipped.
                  </Text>
                </Alert>
              )}
              <Group>
                <Button variant="light" onClick={() => { resetUpload(); setPeriod(null); }}>
                  Import another file
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Paper>

      {/* Commit confirm — names the month (#5); challenges before overwriting (#6). */}
      <Modal
        opened={confirm !== null} onClose={() => setConfirm(null)}
        title={confirm === 'overwrite' ? `Overwrite existing data for ${monthLabel}?` : `Import for ${monthLabel}?`}
      >
        {preview && confirm && (
          <Stack gap="sm">
            {confirm === 'overwrite' ? (
              <Text size="sm">
                There is already EOM data on record for <b>{monthLabel}</b>. Proceeding will{' '}
                <b>overwrite {preview.conflict_count} existing account-month{plural(preview.conflict_count)}</b>
                {preview.new_count > 0 && <> and import <b>{preview.new_count} new</b></>} for {monthLabel}.
                The previous values are replaced and cannot be recovered.
              </Text>
            ) : (
              <Text size="sm">
                Import <b>{preview.new_count} new account-month{plural(preview.new_count)}</b> for <b>{monthLabel}</b>
                {preview.conflict_count > 0 && <>; the {preview.conflict_count} already on record are kept unchanged</>}.
              </Text>
            )}
            {!!preview.material_flow_warnings?.length && (
              <Alert
                color="bordeaux" variant="light" icon={<IconAlertTriangle size={16} />}
                title={`${preview.material_flow_warnings.length} agreement-month return${plural(preview.material_flow_warnings.length)} will be withheld`}
              >
                <Text size="sm">
                  {preview.material_flow_warnings.length} flow{plural(preview.material_flow_warnings.length)} above 10% of
                  opening AuM {preview.material_flow_warnings.length === 1 ? 'has' : 'have'} no mtd%. The AuM &amp; flows
                  will import, but those agreement-months' returns will NOT be calculated until you supply the mtd%.
                  Cancel to fix the file, or Proceed.
                </Text>
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                color={preview.material_flow_warnings?.length ? 'bordeaux' : (confirm === 'overwrite' ? 'orange' : undefined)}
                loading={committing} onClick={() => handleCommit(confirm)}
              >
                {preview.material_flow_warnings?.length
                  ? `Proceed — ${confirm === 'overwrite' ? 'overwrite' : 'import'} for ${monthLabel}`
                  : `${confirm === 'overwrite' ? 'Overwrite' : 'Import'} for ${monthLabel}`}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

/** The change-preview table (Pedro 2026-07-18): what this file would do vs the current
 *  records — New / Existing-to-change / Existing-to-delete by field (AuM · Flows · Returns).
 *  Replaces the old "already present / not in file" prose. "Changed" counts a value that
 *  ACTUALLY differs — including a stored flow/return CLEARED to blank by the file (so the
 *  formerly-silent overwrite-to-null is visible). The "Applies on" column adapts each row to
 *  the action buttons below (New = every mode; Change = Overwrite/Replace; Delete = Replace). */
function ChangePreviewTable({ report }: { report: MetricsImportReport }) {
  const p = (n: number) => (n === 1 ? '' : 's');
  const del = report.delete_count ?? 0;
  const rows = [
    { label: 'New — to be created', applies: 'All modes', c: report.new_by_field },
    { label: 'Existing — to be changed', applies: 'Overwrite · Replace', c: report.changed_by_field },
    { label: 'Existing — to be deleted', applies: 'Replace only', c: report.deleted_by_field },
  ];
  return (
    <Card withBorder padding="sm" bg="var(--mantine-color-gray-0)">
      <Text size="sm" mb="xs">
        Checked against the current records, this file would{' '}
        <b>create {report.new_count}</b> new record{p(report.new_count)} in {report.new_agreements}{' '}
        agreement{p(report.new_agreements)}, <b>change {report.changed_count}</b> in{' '}
        {report.changed_agreements}, and — with <b>Replace history</b> — <b>delete {del}</b> in{' '}
        {report.deleted_agreements}.
      </Text>
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Change</Table.Th>
            <Table.Th>Applies on</Table.Th>
            <Table.Th ta="right">AuM</Table.Th>
            <Table.Th ta="right">Flows</Table.Th>
            <Table.Th ta="right">Returns</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.label}>
              <Table.Td>{r.label}</Table.Td>
              <Table.Td c="dimmed" fz="xs">{r.applies}</Table.Td>
              <Table.Td ta="right">{r.c.aum}</Table.Td>
              <Table.Td ta="right">{r.c.flow}</Table.Td>
              <Table.Td ta="right">{r.c.ret}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed" mt="xs">
        “Changed” counts a value that actually differs — <b>including one cleared to blank</b> by this
        file. A month already on record with identical values isn’t counted.
      </Text>
    </Card>
  );
}

/** Reconciliation summary for the preview: a short matched/unmatched/error line, the
 *  actionable exceptions (blocked / unmatched CIIFs / unreadable rows), and the
 *  change-preview table (New/Change/Delete × AuM/Flows/Returns). */
function ReviewPanel({ report, fileName }: { report: MetricsImportReport; fileName: string }) {
  const matched = report.matched_ciifs.length;
  const unmatched = report.unmatched_ciifs.length;
  const errs = report.errors.length;
  const blocking = report.blocking_errors?.length ?? 0;
  const hasChanges = report.new_count + report.conflict_count + (report.delete_count ?? 0) > 0;
  const plural = (n: number) => (n === 1 ? '' : 's');

  return (
    <Stack gap="sm">
      {fileName && <Text size="sm" c="dimmed">{fileName}</Text>}
      <Text>
        <b>{matched}</b> agreement{plural(matched)} matched ·{' '}
        <b>{unmatched}</b> unmatched ·{' '}
        <b>{errs}</b> error{plural(errs)}
        {blocking > 0 && <>{' · '}<Text span c="red" fw={700}>{blocking} blocked</Text></>}
      </Text>

      {blocking > 0 && (
        <Alert
          color="red" variant="filled" icon={<IconAlertCircle size={16} />}
          title={`Upload blocked — ${blocking} row${plural(blocking)} with a negative AuM`}
        >
          <List size="sm" spacing={2}>
            {report.blocking_errors.slice(0, 50).map((e, i) => <List.Item key={i}>{e}</List.Item>)}
          </List>
          {blocking > 50 && <Text size="xs" mt={4}>…and {blocking - 50} more.</Text>}
          <Text size="sm" mt={6}>
            Fix these in the workbook (AuM cannot be negative; a negative Flow is a valid net
            outflow), then re-upload.
          </Text>
        </Alert>
      )}

      {unmatched > 0 && (
        <Alert
          color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}
          title={`${unmatched} CIIF row${plural(unmatched)} matched no live agreement — ignored`}
        >
          <Text size="sm">{report.unmatched_ciifs.join(', ')}</Text>
        </Alert>
      )}

      {errs > 0 && (
        <Alert
          color="red" variant="light" icon={<IconAlertCircle size={16} />}
          title={`${errs} row${plural(errs)} could not be read — skipped`}
        >
          <List size="sm" spacing={2}>
            {report.errors.slice(0, 50).map((e, i) => <List.Item key={i}>{e}</List.Item>)}
          </List>
          {errs > 50 && <Text size="xs" c="dimmed" mt={4}>…and {errs - 50} more.</Text>}
        </Alert>
      )}

      {hasChanges && <ChangePreviewTable report={report} />}
    </Stack>
  );
}

/** Confirm action depends on conflicts (the locked policy): none → one Import;
 *  conflicts → Overwrite / Import-new-keep / Cancel; nothing → Start over. */
function ConfirmActions({
  report, committing, onCommit, onCancel,
}: {
  report: MetricsImportReport;
  committing: boolean;
  onCommit: (c: MetricsImportConflict) => void;
  onCancel: () => void;
}) {
  const plural = (n: number) => (n === 1 ? '' : 's');

  const [confirmReplace, setConfirmReplace] = useState(false);
  const del = report.delete_count ?? 0;
  const replaceBtn = del > 0 ? (
    <Tooltip
      label={`Makes your file the COMPLETE record for these agreements: overwrites/adds its months and permanently DELETES the ${del} stored month(s) not in the file (audited; cannot be undone in-app).`}
      multiline w={320} withArrow position="top"
    >
      <Button color="red" variant="outline" onClick={() => setConfirmReplace(true)} disabled={committing}>
        Replace history (delete {del})
      </Button>
    </Tooltip>
  ) : null;
  const replaceModal = del > 0 ? (
    <Modal
      opened={confirmReplace} onClose={() => setConfirmReplace(false)} centered
      title="Replace history — confirm deletion"
    >
      <Stack gap="sm">
        <Text size="sm">
          This makes the uploaded file the <b>complete historical record</b> for its agreements:
          overwrite <b>{report.conflict_count}</b>, add <b>{report.new_count}</b>, and{' '}
          <Text span c="red" fw={700}>permanently delete {del} stored month{plural(del)}</Text> not in
          the file.
        </Text>
        {report.delete_sample.length > 0 && (
          <Text size="xs" c="dimmed">
            Will delete (first {report.delete_sample.length} of {del}): {report.delete_sample.join('  ·  ')}
          </Text>
        )}
        <Text size="xs" c="dimmed">Recorded in the audit log; cannot be undone in the app.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setConfirmReplace(false)} disabled={committing}>
            Cancel
          </Button>
          <Button
            color="red" loading={committing}
            onClick={() => { setConfirmReplace(false); onCommit('replace'); }}
          >
            Delete {del} &amp; replace
          </Button>
        </Group>
      </Stack>
    </Modal>
  ) : null;

  if ((report.blocking_errors?.length ?? 0) > 0) {
    return (
      <Group>
        <Text size="sm" c="red">
          Import blocked — fix the flagged negative value{plural(report.blocking_errors.length)} in the
          workbook, then re-upload.
        </Text>
        <Button variant="default" onClick={onCancel}>Start over</Button>
      </Group>
    );
  }

  if (report.new_count === 0 && report.conflict_count === 0) {
    return (
      <Group>
        <Text size="sm" c="dimmed">Nothing to import from this file.</Text>
        <Button variant="default" onClick={onCancel}>Start over</Button>
      </Group>
    );
  }

  if (report.conflict_count === 0) {
    return (
      <>
        <Group>
          <Tooltip label="Adds these new months; nothing already stored is affected." multiline w={300} withArrow position="top">
            <Button onClick={() => onCommit('skip')} loading={committing}>
              Import {report.new_count} new month{plural(report.new_count)}
            </Button>
          </Tooltip>
          {replaceBtn}
          <Button variant="default" onClick={onCancel} disabled={committing}>Cancel</Button>
        </Group>
        {replaceModal}
      </>
    );
  }

  return (
    <>
      <Stack gap="xs">
        <Text size="sm">
          {report.conflict_count} month{plural(report.conflict_count)} already exist. Choose how to proceed:
        </Text>
        <Group>
          <Tooltip label="Updates the months in your file that already exist; other stored months are left untouched." multiline w={300} withArrow position="top">
            <Button color="orange" onClick={() => onCommit('overwrite')} loading={committing}>
              Overwrite existing months
            </Button>
          </Tooltip>
          <Tooltip label="Adds only months not yet stored; existing months keep their current values." multiline w={300} withArrow position="top">
            <Button onClick={() => onCommit('skip')} loading={committing}>
              Import new, keep existing
            </Button>
          </Tooltip>
          {replaceBtn}
          <Button variant="default" onClick={onCancel} disabled={committing}>Cancel</Button>
        </Group>
      </Stack>
      {replaceModal}
    </>
  );
}

/** Commit outcome: "Imported X new · overwrote/kept M · K unmatched ignored". */
function ResultPanel({ report }: { report: MetricsImportReport }) {
  const unmatched = report.unmatched_ciifs.length;
  const plural = (n: number) => (n === 1 ? '' : 's');
  return (
    <Alert color="green" variant="light" icon={<IconCheck size={16} />} title="Import complete">
      <Text size="sm">
        Imported <b>{report.inserted}</b> new month{plural(report.inserted)}.
        {report.overwritten > 0 && <> Overwrote <b>{report.overwritten}</b> existing.</>}
        {report.skipped > 0 && <> Kept <b>{report.skipped}</b> existing (skipped).</>}
        {(report.deleted ?? 0) > 0 && <> Deleted <b>{report.deleted}</b> stored month{plural(report.deleted)} not in the file.</>}
        {unmatched > 0 && <> <b>{unmatched}</b> unmatched CIIF row{plural(unmatched)} ignored.</>}
      </Text>
    </Alert>
  );
}

// ── Section B — manual entry / history (Read ⇄ Edit + dirty guard) ─────────
function ManualEntrySection() {
  const qc = useQueryClient();
  const { confirmDiscardAsync, registerDirty } = useDirtyForm();
  const { confirm } = useAlertModal();

  // 200 is the endpoint's max (Query le=200) and covers the firm's agreement
  // count comfortably; if it ever exceeds 200, paginate here.
  const agreementsQuery = useQuery({
    queryKey: ['agreements', 'for-metrics'],
    queryFn: () => agreementsApi.list({ limit: 200 }),
  });

  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [month, setMonth] = useState<Date | null>(null);
  const [editing, setEditing] = useState(false);
  // F3 (C-172): the section opens in Read (history) mode; the entry affordances
  // (month picker + row-click-to-edit) are gated behind the Edit button below.
  const [manualMode, setManualMode] = useState(false);
  const baseline = useRef<EditVals>(BLANK_VALS);

  const [aum, setAum] = useState<number | string>('');
  const [flow, setFlow] = useState<number | string>('');   // signed net (+ in / − out)
  const [ret, setRet] = useState<number | string>('');

  const seriesQuery = useQuery({
    queryKey: ['monthly-metrics', agreementId],
    queryFn: () => monthlyMetricsApi.getSeries(agreementId as string),
    enabled: !!agreementId,
  });
  const series = seriesQuery.data ?? [];

  const existing = useMemo(
    () => (month ? series.find((r) => monthKeyOf(r.period_month) === ym(month)) ?? null : null),
    [series, month],
  );

  // The API returns the series oldest-first; show it newest-first in the table.
  const seriesDesc = useMemo(
    () => [...series].sort((a, b) => b.period_month.localeCompare(a.period_month)),
    [series],
  );

  // Dirty = in Edit mode AND the form diverges from the baseline it was loaded
  // with. Published to the global registry so route navigation away triggers
  // the standard "Unsaved changes" guard (and anyDirty() picks it up).
  const dirty = editing && !sameVals({ aum, flow, ret }, baseline.current);
  useEffect(() => registerDirty('portfolio-month-editor', dirty), [dirty, registerDirty]);

  function applyVals(v: EditVals) {
    setAum(v.aum); setFlow(v.flow); setRet(v.ret);
  }
  function enterEdit(vals: EditVals) {
    applyVals(vals);
    baseline.current = vals;
    setEditing(true);
  }
  function toRead() {
    setEditing(false);
    setMonth(null);
    applyVals(BLANK_VALS);
    baseline.current = BLANK_VALS;
  }

  // Any transition that would lose an in-progress edit goes through the guard
  // (confirmDiscardAsync no-ops when nothing is dirty).
  async function guarded(action: () => void) {
    if (await confirmDiscardAsync()) action();
  }

  // Leave Edit mode back to Read; guarded so an in-progress edit prompts first.
  function exitManual() {
    void guarded(() => { setManualMode(false); toRead(); });
  }

  function pickAgreement(id: string | null) {
    void guarded(() => {
      setAgreementId(id);
      setEditing(false);
      setMonth(null);
      applyVals(BLANK_VALS);
      baseline.current = BLANK_VALS;
    });
  }
  function pickMonth(d: Date | null) {
    void guarded(() => {
      setMonth(d);
      if (!d) { setEditing(false); applyVals(BLANK_VALS); baseline.current = BLANK_VALS; return; }
      const row = series.find((r) => monthKeyOf(r.period_month) === ym(d)) ?? null;
      enterEdit(row ? valsFromRow(row) : BLANK_VALS);
    });
  }
  function editRow(row: AgreementMonthlyMetric) {
    // Legacy computed-era rows (pre-firebreak) are read-only: agreement figures from
    // the cut-off on are derived from per-account inputs, not edited here (§3h/§3j).
    if (isComputedEraMonth(monthKeyOf(row.period_month))) {
      notifications.show({
        color: 'blue', title: 'Derived from per-account data',
        message: `${monthKeyOf(row.period_month)} is computed from per-account inputs (Jan-2026 onward) and can't be edited here. Enter per-account EOM AuM & flows in the section below; view results in Returns.`,
      });
      return;
    }
    void guarded(() => {
      setMonth(new Date(`${row.period_month}T00:00:00`));
      enterEdit(valsFromRow(row));
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const cents = toCents(aum);
      if (cents == null) throw new Error('AuM is required.');
      // Split the signed net flow back into the stored inflow/outflow magnitudes
      // (blank → both null; + → inflow; − → outflow).
      const flowCents = toCents(flow);
      return monthlyMetricsApi.upsertMonth({
        agreement_id: agreementId as string,
        period_month: `${ym(month as Date)}-01`,
        aum_eur_cents: cents,
        inflow_eur_cents: flowCents == null ? null : Math.max(flowCents, 0),
        outflow_eur_cents: flowCents == null ? null : Math.max(-flowCents, 0),
        // Fee ex-VAT isn't edited here (Portfolio Data = EOM metrics; fees live on the
        // Fees page). Preserve the stored value so an AuM/flows/return edit never wipes
        // the historical fee actual.
        fee_ex_vat_cents: existing ? existing.fee_ex_vat_cents : null,
        return_ratio: pctToRatio(ret),
        source: 'manual',
      });
    },
    onSuccess: (_data, _vars, _ctx) => {
      const label = existing ? 'Updated' : 'Added';
      const m = month ? ym(month) : '';
      notifications.show({ color: 'green', title: 'Saved', message: `${label} ${m}.` });
      qc.invalidateQueries({ queryKey: ['monthly-metrics', agreementId] });
      toRead();
    },
    onError: (err) => {
      notifications.show({
        color: 'red', title: 'Save failed',
        message: err instanceof Error ? err.message : 'Could not save the month.',
      });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => monthlyMetricsApi.deleteMonth(id),
    onSuccess: () => {
      notifications.show({
        color: 'green', title: 'Deleted',
        message: `Removed ${month ? ym(month) : 'the month'}.`,
      });
      qc.invalidateQueries({ queryKey: ['monthly-metrics', agreementId] });
      toRead();
    },
    onError: (err) => {
      notifications.show({
        color: 'red', title: 'Delete failed',
        message: err instanceof Error ? err.message : 'Could not delete the month.',
      });
    },
  });

  // Consequential + irreversible → Edit-gated (only reachable here), existing
  // rows only, and committed through its OWN named confirm — never a Save (C-172).
  async function handleDelete() {
    if (!existing || !month) return;
    const label = agreementOptions.find((o) => o.value === agreementId)?.label ?? 'this agreement';
    const ok = await confirm({
      title: 'Delete stored month',
      message: `Delete the stored metrics for ${label} — ${ym(month)}? This can't be undone.`,
      destructive: true,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (ok) del.mutate(existing.id);
  }

  const agreementOptions = useMemo(() => (
    (agreementsQuery.data?.items ?? [])
      .map((a) => ({ value: a.id, label: `${a.code_1} — ${a.client_mnemonic}` }))
      .sort((x, y) => x.label.localeCompare(y.label))
  ), [agreementsQuery.data]);

  const canSave = editing && !!month && aum !== '' && dirty && !save.isPending;

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Title order={4}>Manual entry &amp; history</Title>
            <Text c="dimmed" size="sm">
              Pick an agreement to view its stored months (read-only). Press Edit to add
              or correct one. Manual entry is for historical months only (before Jan-2026);
              from Jan-2026, agreement AuM, flows and returns are derived from per-account
              data — enter those in the EOM AuM &amp; flows tab. Money is in euros; return
              is a percentage.
            </Text>
          </div>
          {!manualMode ? (
            <Button
              variant="light"
              leftSection={<IconPencil size={16} />}
              onClick={() => setManualMode(true)}
              disabled={!agreementId}
              style={{ flexShrink: 0 }}
            >
              Edit
            </Button>
          ) : (
            <Button variant="default" onClick={exitManual} style={{ flexShrink: 0 }}>Done</Button>
          )}
        </Group>

        <Group grow align="flex-start">
          <Select
            label="Agreement"
            placeholder={agreementsQuery.isLoading ? 'Loading…' : 'Pick an agreement'}
            data={agreementOptions}
            value={agreementId}
            onChange={pickAgreement}
            searchable
            nothingFoundMessage="No match"
            disabled={agreementsQuery.isLoading}
          />
          {manualMode && (
            <MonthPickerInput
              label="Month"
              placeholder="Pick a month to add or edit"
              description="Historical months only (before Jan-2026)"
              inputWrapperOrder={['label', 'input', 'description']}
              value={month}
              onChange={pickMonth}
              maxDate={LAST_HISTORICAL_MONTH}
              disabled={!agreementId || seriesQuery.isLoading}
            />
          )}
        </Group>

        {editing && month && (
          <Card withBorder padding="md" bg="var(--mantine-color-gray-0)">
            <Stack gap="sm">
              <Group gap="xs">
                {existing
                  ? <Badge color="blue" variant="light">Editing {ym(month)} · stored ({existing.source})</Badge>
                  : <Badge color="green" variant="light">New month {ym(month)}</Badge>}
              </Group>
              <Group grow align="flex-start">
                <NumberInput label="AuM (€)" value={aum} onChange={setAum}
                  thousandSeparator="," decimalScale={2} min={0} allowNegative={false} withAsterisk />
                <NumberInput label="Net flow (€)" description="+ inflow / − outflow" value={flow} onChange={setFlow}
                  thousandSeparator="," decimalScale={2} allowNegative
                  inputWrapperOrder={['label', 'input', 'description']} />
                <NumberInput label="Return (%)" value={ret} onChange={setRet}
                  decimalScale={4} suffix="%" />
              </Group>
              <Group justify="space-between">
                <Group>
                  <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave || del.isPending}>
                    {existing ? 'Save changes' : 'Add month'}
                  </Button>
                  <Button variant="default" onClick={toRead} disabled={save.isPending || del.isPending}>Cancel</Button>
                </Group>
                {existing && (
                  <Button
                    color="red"
                    variant="outline"
                    leftSection={<IconTrash size={16} />}
                    onClick={handleDelete}
                    loading={del.isPending}
                    disabled={save.isPending}
                  >
                    Delete this month
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>
        )}

        {agreementId && (
          <>
            <Divider label="Stored months" labelPosition="left" />
            {seriesQuery.isLoading ? (
              <Text c="dimmed" size="sm">Loading…</Text>
            ) : series.length === 0 ? (
              <Text c="dimmed" size="sm">No months stored for this agreement yet.</Text>
            ) : (
              <>
                <Table.ScrollContainer minWidth={700}>
                  <Table striped highlightOnHover={manualMode} withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Month</Table.Th>
                        <Table.Th ta="right">AuM</Table.Th>
                        <Table.Th ta="right">Net flow</Table.Th>
                        <Table.Th ta="right">Return</Table.Th>
                        <Table.Th>Source</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {seriesDesc.map((r) => {
                        const isCurrent = editing && month != null && monthKeyOf(r.period_month) === ym(month);
                        return (
                          <Table.Tr
                            key={r.id}
                            onClick={manualMode ? () => editRow(r) : undefined}
                            style={{
                              cursor: manualMode ? 'pointer' : 'default',
                              background: isCurrent ? 'var(--mantine-color-blue-0)' : undefined,
                            }}
                          >
                            <Table.Td>{monthKeyOf(r.period_month)}</Table.Td>
                            <Table.Td ta="right">{fmtEur(r.aum_eur_cents)}</Table.Td>
                            <Table.Td ta="right">{fmtEur(netFlowCents(r))}</Table.Td>
                            <Table.Td ta="right">{fmtPct(r.return_ratio)}</Table.Td>
                            <Table.Td>{r.source}</Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
                {manualMode && (
                  <Text c="dimmed" size="xs">Tip: click a row to edit that month, or pick a month above to add one.</Text>
                )}
              </>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}
